/**
 * Pure game rules. See docs/IMPLEMENTATION_PLAN.md §3.
 *
 * HARD CONSTRAINT (spec §5): this module imports nothing from `worker/` or
 * `client/`, touches no Cloudflare runtime, and performs no I/O or randomness.
 * Everything here is a function of its arguments, so it is exhaustively testable
 * on plain Node. Dice rolling lives in the Durable Object, not here — it needs
 * `crypto.getRandomValues` and must never be reachable from a client.
 */

import { FACES, type Bid, type BidValue, type Face } from './types';

export type Hands = Readonly<Record<string, readonly Face[]>>;

export type RaiseContext = {
  /** Total dice on the table right now. A bid may not exceed this (§3.2). */
  totalDice: number;
  /**
   * Palifico round (§3.5): 1s are not wild, and after the opening bid the face
   * is locked — only the quantity may rise. Off in v1.
   */
  palifico?: boolean;
};

// ---------------------------------------------------------------------------
// Counting
// ---------------------------------------------------------------------------

export function isFace(value: unknown): value is Face {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 6
  );
}

/** Flattens every player's hand into one array of dice on the table. */
export function allDiceFrom(hands: Hands): Face[] {
  return Object.values(hands).flatMap((hand) => [...hand]);
}

export function totalDiceIn(hands: Hands): number {
  let total = 0;
  for (const hand of Object.values(hands)) total += hand.length;
  return total;
}

/**
 * How many dice count toward `face` (§3.2).
 *
 * 1s are wild, so `count(face) = dice showing face + dice showing 1` — except
 * for face 1 itself, which counts only actual 1s. In a palifico round 1s are
 * not wild, so pass `wildOnes: false` and every face counts only itself.
 */
export function countFace(
  dice: readonly Face[],
  face: Face,
  wildOnes = true,
): number {
  let count = 0;
  for (const die of dice) {
    if (die === face) count += 1;
    else if (wildOnes && face !== 1 && die === 1) count += 1;
  }
  return count;
}

// ---------------------------------------------------------------------------
// Bid legality
// ---------------------------------------------------------------------------

/** Structural validity, independent of any current bid. */
export function isValidBidShape(bid: BidValue, totalDice: number): boolean {
  return (
    isFace(bid.face) &&
    typeof bid.quantity === 'number' &&
    Number.isInteger(bid.quantity) &&
    bid.quantity >= 1 &&
    bid.quantity <= totalDice
  );
}

/**
 * Is `next` a legal raise over `current`? (§3.3)
 *
 * `current === null` means this is the round's opening bid, which may be any
 * face 2–6 — never 1s.
 *
 * | current.face | next.face | condition                                  |
 * |--------------|-----------|--------------------------------------------|
 * | ≠ 1          | ≠ 1       | next.q > cur.q, or equal q and higher face  |
 * | ≠ 1          | = 1       | next.q >= ceil(cur.q / 2)                   |
 * | = 1          | = 1       | next.q > cur.q                              |
 * | = 1          | ≠ 1       | next.q >= (2 * cur.q) + 1                   |
 */
export function isLegalRaise(
  current: BidValue | null,
  next: BidValue,
  ctx: RaiseContext,
): boolean {
  if (!isValidBidShape(next, ctx.totalDice)) return false;

  // Opening bid of the round.
  if (current === null) return next.face !== 1;

  if (ctx.palifico === true) {
    // Face is locked after the opening bid; only quantity may rise.
    return next.face === current.face && next.quantity > current.quantity;
  }

  if (current.face !== 1 && next.face !== 1) {
    return (
      next.quantity > current.quantity ||
      (next.quantity === current.quantity && next.face > current.face)
    );
  }

  if (current.face !== 1 && next.face === 1) {
    return next.quantity >= Math.ceil(current.quantity / 2);
  }

  if (current.face === 1 && next.face === 1) {
    return next.quantity > current.quantity;
  }

  // current.face === 1 && next.face !== 1
  return next.quantity >= 2 * current.quantity + 1;
}

/**
 * Every legal raise over `current`, in ascending (quantity, face) order.
 * Small by construction — at most 6 × totalDice candidates.
 */
export function legalRaises(
  current: BidValue | null,
  ctx: RaiseContext,
): BidValue[] {
  const out: BidValue[] = [];
  for (let quantity = 1; quantity <= ctx.totalDice; quantity += 1) {
    for (const face of FACES) {
      const candidate = { quantity, face };
      if (isLegalRaise(current, candidate, ctx)) out.push(candidate);
    }
  }
  return out;
}

/**
 * The raise to offer as the UI default and to auto-play on turn timeout (§9).
 * `null` when no legal raise exists, which is the signal to auto-challenge.
 *
 * Deliberately biased away from 1s: switching to wilds is allowed at a *lower*
 * quantity (`ceil(q/2)`), so a naive "lowest quantity wins" rule would make the
 * timeout bot bid 1s almost every time — a strong strategic move nobody chose.
 * So: lowest quantity then lowest face among non-1 bids, and 1s only when no
 * non-1 raise is legal at all.
 *
 * This terminates. Once nothing but 1s is available it walks q upward to
 * `totalDice`, after which no raise is legal and the caller challenges.
 *
 * Derived by filtering `isLegalRaise` rather than by reimplementing §3.3, so the
 * default bid and the validator cannot drift apart.
 */
export function minLegalRaise(
  current: BidValue | null,
  ctx: RaiseContext,
): BidValue | null {
  const candidates = legalRaises(current, ctx);
  return (
    candidates.find((bid) => bid.face !== 1) ?? candidates[0] ?? null
  );
}

// ---------------------------------------------------------------------------
// Challenge resolution
// ---------------------------------------------------------------------------

export type ChallengeResult = {
  /** Dice matching the bid face, wild 1s included unless palifico. */
  actual: number;
  /** True when the bid held up, i.e. the challenger was wrong. */
  bidWasGood: boolean;
  /** Who loses a die. */
  loserId: string;
};

/**
 * Resolve a challenge (§3.4). `hands` must contain every player still in the
 * game — the count is over all dice on the table, not just the two involved.
 */
export function resolveChallenge(
  hands: Hands,
  bid: Bid,
  challengerId: string,
  options: { palifico?: boolean } = {},
): ChallengeResult {
  const actual = countFace(
    allDiceFrom(hands),
    bid.face,
    options.palifico !== true,
  );
  const bidWasGood = actual >= bid.quantity;

  return {
    actual,
    bidWasGood,
    // Bid good → the challenger was wrong and pays. Bid short → the bidder pays.
    loserId: bidWasGood ? challengerId : bid.playerId,
  };
}

export type ExactResult = {
  actual: number;
  exact: boolean;
  /** Everyone who loses a die. */
  loserIds: string[];
};

/**
 * Resolve a call of "exact" / calza (§3.5, behind `allowExact`, off in v1).
 * Hitting the quantity exactly costs every *other* player a die; missing costs
 * only the caller.
 */
export function resolveExact(
  hands: Hands,
  bid: Bid,
  callerId: string,
  options: { palifico?: boolean } = {},
): ExactResult {
  const actual = countFace(
    allDiceFrom(hands),
    bid.face,
    options.palifico !== true,
  );
  const exact = actual === bid.quantity;

  return {
    actual,
    exact,
    loserIds: exact
      ? Object.keys(hands).filter((id) => id !== callerId)
      : [callerId],
  };
}

// ---------------------------------------------------------------------------
// Turn order
// ---------------------------------------------------------------------------

/**
 * The next player clockwise from `fromId` who is still in `remaining`,
 * `fromId` itself excluded. `null` if nobody qualifies.
 */
export function nextActivePlayer(
  seatOrder: readonly string[],
  fromId: string,
  remaining: Iterable<string>,
): string | null {
  const alive = new Set(remaining);
  const start = seatOrder.indexOf(fromId);
  if (start === -1) return null;

  // Strictly fewer steps than there are seats, so a full lap never lands back
  // on `fromId`. If they are the last player standing the answer is `null`, and
  // the caller has to treat that as game over rather than as "your turn again".
  for (let step = 1; step < seatOrder.length; step += 1) {
    const id = seatOrder[(start + step) % seatOrder.length];
    if (id !== undefined && alive.has(id)) return id;
  }
  return null;
}

/**
 * Who starts the next round (§3.4.6): the player who just lost a die, or — if
 * that loss eliminated them — the next player clockwise who is still in.
 * `null` once nobody is left, i.e. the game is over.
 */
export function nextStarter(
  seatOrder: readonly string[],
  loserId: string,
  remaining: Iterable<string>,
): string | null {
  const alive = new Set(remaining);
  if (alive.has(loserId)) return loserId;
  return nextActivePlayer(seatOrder, loserId, alive);
}
